import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import { MODULO_LABEL, formatMoney, type Plano } from '../subscricoesModel'
import PlanoFormDialog from '../components/PlanoFormDialog'

export default function PlanosList() {
    const { notify } = useNotification()

    const [rows, setRows] = useState<Plano[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-planos:table-page', 20)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<Plano | null>(null)

    const fetch = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Page<Plano>>('/api/admin/planos', { params: { page, size: rowsPerPage, sort: 'preco,asc' } })
            .then((r) => {
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => setError('Não foi possível carregar os planos.'))
            .finally(() => setIsLoading(false))
    }, [page, rowsPerPage])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch alterna a sua própria flag de loading
        fetch()
    }, [fetch])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    const remover = async (p: Plano) => {
        if (!window.confirm(`Remover o plano "${p.nome}"?`)) return
        try {
            await api.delete(`/api/admin/planos/${p.id}`)
            notify('Plano removido.')
            fetch()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível remover.', 'error')
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Planos</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} {total === 1 ? 'plano' : 'planos'}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true) }}>
                    Novo plano
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell align="right">Preço</TableCell>
                                <TableCell align="right">Unidades</TableCell>
                                <TableCell align="right">Utilizadores</TableCell>
                                <TableCell>Módulos</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acções</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Ainda não há planos.</TableCell></TableRow>
                            ) : (
                                rows.map((p) => (
                                    <TableRow key={p.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{p.nome}</TableCell>
                                        <TableCell align="right">{formatMoney(p.preco)}</TableCell>
                                        <TableCell align="right">{p.limiteUnidades > 0 ? p.limiteUnidades : 'Ilimitado'}</TableCell>
                                        <TableCell align="right">{p.limiteUtilizadores > 0 ? p.limiteUtilizadores : 'Ilimitado'}</TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            {p.modulosDisponiveis.length === 0 ? (
                                                <Typography variant="caption" color="text.secondary">Nenhum</Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {p.modulosDisponiveis.map((m) => (
                                                        <Chip key={m} label={MODULO_LABEL[m]} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    size="small"
                                                    label={p.activo ? 'Activo' : 'Inactivo'}
                                                    sx={{ fontWeight: 600, bgcolor: p.activo ? '#F0FDF4' : '#F3F4F6', color: p.activo ? '#15803D' : '#6B7280' }}
                                                />
                                                {!p.cobravel && (
                                                    <Chip
                                                        size="small"
                                                        label="Demo"
                                                        sx={{ fontWeight: 600, bgcolor: 'brand.teal50', color: '#4338CA' }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => { setEditing(p); setFormOpen(true) }} aria-label="Editar">
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={p.emUso ? 'Plano com subscrições — desactive-o' : 'Remover'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'error.main' }}
                                                        disabled={p.emUso}
                                                        onClick={() => remover(p)}
                                                        aria-label="Remover"
                                                    >
                                                        <DeleteForeverOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>

            <PlanoFormDialog
                open={formOpen}
                initial={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => { setFormOpen(false); notify(editing ? 'Plano actualizado.' : 'Plano criado.'); fetch() }}
            />
        </Box>
    )
}
